/**
 * Health Dashboard — channel circuit cards (primary/fallback provider, reset),
 * queue stat cards, delivery line chart (12h/24h/7d), and by-channel/by-source
 * donuts. Ported from the console; charts via react-native-svg.
 */
import React, { useEffect, useState } from 'react';
import { View, Text, ScrollView, StyleSheet } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useResponsive } from '../lib/responsive';
import { useToast } from '../components/toast';
import Icon, { IconName } from '../components/notif/Icon';
import { CircuitBadge } from '../components/notif/badges';
import { PageHeader, SectionLabel, Btn, EmptyState } from '../components/notif/ui';
import { Donut, DeliveryLines } from '../components/notif/charts';
import { getHealth, getCircuitHealth, resetCircuit, getDeliveryMetrics, DeliveryMetrics } from '../services/notification/health';
import { handleApiError } from '../utils/errorHandler';
import { useT } from '../i18n/useT';
import type { ChannelType, CircuitState, BackendHealthResponse, BackendCircuitHealthResponse } from '../schema/notification';

const CHANNELS: ChannelType[] = ['email', 'whatsapp', 'sms', 'push'];
const CH_ICON: Record<ChannelType, IconName> = { email: 'mail', whatsapp: 'message', sms: 'phone', push: 'bell' };
const CHANNEL_COLORS: Record<string, string> = { email: '#1E3A5F', whatsapp: '#25D366', sms: '#6A1B9A', push: '#C2410C' };
const SOURCE_COLORS = ['#1D4ED8', '#16A34A', '#6B21A8', '#E65100', '#0891B2', '#B45309'];
const PROVIDER_LABELS: Record<string, string> = {
  smtp: 'SMTP', ses: 'AWS SES', sendgrid: 'SendGrid', meta: 'Meta Cloud API',
  twilio: 'Twilio', plivo: 'Plivo', onesignal: 'OneSignal', fcm: 'FCM', apns: 'APNs',
};
const providerLabel = (key?: string) => (key ? PROVIDER_LABELS[key] ?? key : '—');

const StatCard: React.FC<{ label: string; value: number; sub?: string; tone?: 'error'; action?: string; onAction?: () => void; progress?: number }> = ({ label, value, sub, tone, action, onAction, progress }) => {
  const { theme: { colors } } = useTheme();
  return (
    <View style={[h.stat, { backgroundColor: colors.card, borderColor: colors.line }]}>
      <Text style={{ color: colors.ink3, fontSize: 12 }}>{label}</Text>
      <Text style={{ color: tone === 'error' ? colors.danger : colors.ink, fontSize: 28, fontWeight: '700', marginVertical: 2 }}>{value.toLocaleString()}</Text>
      {!!sub && <Text style={{ color: colors.ink4, fontSize: 11 }}>{sub}</Text>}
      {progress != null && (
        <View style={{ height: 6, backgroundColor: colors.paper3, borderRadius: 2, marginTop: 8, overflow: 'hidden' }}>
          <View style={{ width: `${Math.min(100, progress)}%`, height: '100%', backgroundColor: colors.success }} />
        </View>
      )}
      {action && <Text onPress={onAction} style={{ color: colors.accent, fontSize: 12, marginTop: 10 }}>{action} →</Text>}
    </View>
  );
};

export default function HealthScreen() {
  const { theme: { colors } } = useTheme();
  const { pad } = useResponsive();
  const toast = useToast();
  const t = useT();
  const CH_NAME: Record<ChannelType, string> = {
    email: t('health.channel.email'), whatsapp: t('health.channel.whatsapp'),
    sms: t('health.channel.sms'), push: t('health.channel.push'),
  };
  const [metricsWindow, setMetricsWindow] = useState('12h');
  const [health, setHealth] = useState<BackendHealthResponse | null>(null);
  const [circuit, setCircuit] = useState<BackendCircuitHealthResponse | null>(null);
  const [metrics, setMetrics] = useState<DeliveryMetrics | null>(null);

  const loadAll = () => {
    getHealth().then(setHealth).catch(() => {});
    getCircuitHealth().then(setCircuit).catch(() => {});
  };
  useEffect(loadAll, []);
  useEffect(() => { getDeliveryMetrics(metricsWindow).then(setMetrics).catch(() => {}); }, [metricsWindow]);

  const resetCb = (channel: ChannelType) => {
    resetCircuit(channel)
      .then(() => { toast.success(t('health.toastCircuitReset', { channel }), { message: t('health.toastCircuitResetBody') }); loadAll(); })
      .catch(err => handleApiError(err, t('health.errResetFailed')));
  };

  const queue = health?.queue ?? { waiting: 0, active: 0, delayed: 0, failed: 0 };
  const ts = metrics?.timeSeries;
  const hasChart = !!ts && ts.delivered.length > 1;
  const channelSegments = (metrics?.byChannel ?? []).map((c, i) => ({ label: c.label.charAt(0).toUpperCase() + c.label.slice(1), value: c.value, color: CHANNEL_COLORS[c.label] ?? SOURCE_COLORS[i % SOURCE_COLORS.length] }));
  const sourceSegments = (metrics?.bySource ?? []).map((sg, i) => ({ label: sg.label, value: sg.value, color: SOURCE_COLORS[i % SOURCE_COLORS.length] }));
  const windowLabel: Record<string, string> = { '12h': t('health.window12h'), '24h': t('health.window24h'), '7d': t('health.window7d') };

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: pad, gap: 8 }}>
      <PageHeader title={t('health.title')} subtitle={t('health.subtitle')} right={<Btn label={t('common.refresh')} icon="refresh" size="sm" onPress={loadAll} />} />

      <SectionLabel>{t('health.channelStatus')}</SectionLabel>
      <View style={h.grid2}>
        {CHANNELS.map(ch => {
          const snap = circuit?.states?.[ch];
          const state: CircuitState = (snap?.state as CircuitState) || 'CLOSED';
          const prov = circuit?.providers?.[ch];
          const isOpen = state === 'OPEN';
          return (
            <View key={ch} style={[h.chCard, { backgroundColor: colors.card, borderColor: isOpen ? colors.dangerLine : colors.line }]}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <View style={[h.chIcon, { backgroundColor: colors.paper2 }]}><Icon name={CH_ICON[ch]} size={18} color={colors.ink2} /></View>
                <View style={{ flex: 1 }}>
                  <Text style={{ color: colors.ink, fontSize: 14, fontWeight: '600' }}>{CH_NAME[ch]}</Text>
                  <Text style={{ color: colors.ink4, fontSize: 11 }}>{t('health.failuresLastWindow', { count: snap?.failureCount || 0 })}</Text>
                </View>
                <CircuitBadge state={state} />
              </View>
              <View style={{ gap: 8 }}>
                <View style={h.provRow}>
                  <View><Text style={{ color: colors.ink4, fontSize: 11, textTransform: 'uppercase' }}>{t('health.primary')}</Text><Text style={{ color: colors.ink2, fontSize: 13 }}>{providerLabel(prov?.primary)}</Text></View>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}><View style={[h.dot, { backgroundColor: isOpen ? colors.danger : colors.success }]} /><Text style={{ color: colors.ink3, fontSize: 12 }}>{isOpen ? t('health.failing') : t('health.active')}</Text></View>
                </View>
                <View style={h.provRow}>
                  <View><Text style={{ color: colors.ink4, fontSize: 11, textTransform: 'uppercase' }}>{t('health.fallback')}</Text><Text style={{ color: colors.ink2, fontSize: 13 }}>{providerLabel(prov?.fallback)}</Text></View>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}><View style={[h.dot, { backgroundColor: isOpen ? colors.success : colors.ink4 }]} /><Text style={{ color: colors.ink3, fontSize: 12 }}>{isOpen ? t('health.servingAsFallback') : t('health.standby')}</Text></View>
                </View>
              </View>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, paddingTop: 10, borderTopWidth: 1, borderTopColor: colors.line }}>
                <Text style={{ color: colors.ink4, fontSize: 11 }}>{snap?.lastStateChange ? t('health.changedAt', { time: new Date(snap.lastStateChange).toLocaleTimeString() }) : t('health.noRecentChange')}</Text>
                <Btn label={t('health.resetCircuit')} icon="refresh" size="sm" kind={isOpen ? 'outline' : 'ghost'} disabled={state === 'CLOSED'} onPress={() => resetCb(ch)} />
              </View>
            </View>
          );
        })}
      </View>

      <SectionLabel>{t('health.queueStatus')}</SectionLabel>
      <View style={{ flexDirection: 'row', gap: 12, flexWrap: 'wrap' }}>
        <StatCard label={t('health.activeJobs')} value={queue.active} sub={t('health.currentlyProcessing')} />
        <StatCard label={t('health.delayedJobs')} value={queue.delayed} sub={t('health.againstDelayedCap')} progress={(queue.delayed / 5000) * 100} />
        <StatCard label={t('health.failedJobs')} value={queue.failed} tone="error" sub={t('health.last24h')} action={t('health.viewAllInLog')} onAction={() => { /* nav handled by sidebar */ }} />
      </View>

      <SectionLabel>{t('health.deliveryMetrics', { window: windowLabel[metricsWindow] ?? metricsWindow })}</SectionLabel>
      <View style={[h.chartCard, { backgroundColor: colors.card, borderColor: colors.line }]}>
        <View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 6, marginBottom: 10 }}>
          {(['12h', '24h', '7d'] as const).map(w => (
            <Text key={w} onPress={() => setMetricsWindow(w)} style={[h.winChip, { color: metricsWindow === w ? colors.accentInk : colors.ink3, backgroundColor: metricsWindow === w ? colors.accentSoft : 'transparent', borderColor: colors.line }]}>{w}</Text>
          ))}
        </View>
        {hasChart ? (
          <>
            <DeliveryLines delivered={ts!.delivered} partial={ts!.partial} failed={ts!.failed} />
            <View style={{ flexDirection: 'row', gap: 16, marginTop: 10, justifyContent: 'center' }}>
              {([[t('health.legendDelivered'), '#2E7D32'], [t('health.legendPartial'), '#F57C00'], [t('health.legendFailed'), '#C0392B']] as const).map(([l, c]) => (
                <View key={l} style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}><View style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: c }} /><Text style={{ color: colors.ink3, fontSize: 11 }}>{l}</Text></View>
              ))}
            </View>
          </>
        ) : (
          <EmptyState title={t('health.noDeliveryData')} body={t('health.noDeliveryDataBody')} />
        )}
      </View>

      <View style={[h.grid2, { marginTop: 8 }]}>
        <Donut title={t('health.byChannel')} segments={channelSegments.length ? channelSegments : [{ label: t('health.noDataYet'), value: 1, color: '#E2E8F0' }]} />
        <Donut title={t('health.bySource')} segments={sourceSegments.length ? sourceSegments : [{ label: t('health.noDataYet'), value: 1, color: '#E2E8F0' }]} />
      </View>
    </ScrollView>
  );
}

const h = StyleSheet.create({
  grid2: { flexDirection: 'row', gap: 12, flexWrap: 'wrap' },
  stat: { flex: 1, minWidth: 180, padding: 16, borderRadius: 2, borderWidth: 1 },
  chCard: { flex: 1, minWidth: 300, padding: 16, borderRadius: 2, borderWidth: 1 },
  chIcon: { width: 36, height: 36, borderRadius: 2, alignItems: 'center', justifyContent: 'center' },
  provRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  dot: { width: 8, height: 8, borderRadius: 4 },
  chartCard: { padding: 16, borderRadius: 2, borderWidth: 1 },
  winChip: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 2, borderWidth: 1, fontSize: 12, overflow: 'hidden' },
});
